/**
 * Seeded pseudo-random helpers for deterministic fixtures.
 *
 * Every generator in this directory derives its randomness from a string seed so the same tenant,
 * dataset and index always yield the same record, regardless of the order in which pages are
 * requested. Nothing here is cryptographic.
 */
import { sha256Hex } from '../domain/hash.js';

/** FNV-1a 32-bit hash of a string (cheap, stable, good enough to seed mulberry32). */
export function hashString(input: string, seed = 0x811c9dc5): number {
  let h = seed >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** mulberry32: 32-bit state, returns floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type SeedPart = string | number;

function joinSeed(parts: readonly SeedPart[]): string {
  return parts.map((p) => String(p)).join('\u001f');
}

/** Small deterministic random source with the helpers the generators need. */
export class Rng {
  private readonly next32: () => number;
  readonly seed: string;

  constructor(seed: SeedPart | readonly SeedPart[]) {
    this.seed = Array.isArray(seed) ? joinSeed(seed as readonly SeedPart[]) : String(seed);
    this.next32 = mulberry32(hashString(this.seed));
  }

  /** Float in [0, 1). */
  next(): number {
    return this.next32();
  }

  /** Integer in [min, max] (inclusive). */
  int(min: number, max: number): number {
    if (max < min) throw new RangeError(`int: max ${max} < min ${min}`);
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** True with probability `p`. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  bool(): boolean {
    return this.chance(0.5);
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('pick: empty list');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Weighted pick: entries are [value, weight]; weights need not sum to 1. */
  pickWeighted<T>(entries: readonly (readonly [T, number])[]): T {
    let total = 0;
    for (const [, w] of entries) total += w;
    if (total <= 0) throw new RangeError('pickWeighted: weights must be positive');
    let r = this.next() * total;
    for (const [value, w] of entries) {
      r -= w;
      if (r < 0) return value;
    }
    return entries[entries.length - 1]![0];
  }

  /** Fisher-Yates shuffle into a new array. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = out[i] as T;
      out[i] = out[j] as T;
      out[j] = tmp;
    }
    return out;
  }

  /** `n` distinct items (or all of them when n >= length), order randomised. */
  sample<T>(items: readonly T[], n: number): T[] {
    if (n >= items.length) return this.shuffle(items);
    return this.shuffle(items).slice(0, Math.max(0, n));
  }

  /** Lowercase hex string of `length` characters. */
  hex(length: number): string {
    let out = '';
    while (out.length < length) out += Math.floor(this.next() * 0x100000000).toString(16).padStart(8, '0');
    return out.slice(0, length);
  }

  /** Child generator whose stream is independent of how much of this one was consumed. */
  fork(...label: readonly SeedPart[]): Rng {
    return new Rng([this.seed, ...label]);
  }
}

/** Generator for a fully-qualified seed path, e.g. rngFor(tenantSeed, 'people', 42). */
export function rngFor(...parts: readonly SeedPart[]): Rng {
  return new Rng(parts);
}

/**
 * Deterministic UUID v4-shaped identifier derived from a seed string (sha256 truncated to 128
 * bits, version nibble 4, RFC 4122 variant). Stable across processes and machines.
 */
export function uuidFromSeed(seed: string): string {
  const hex = sha256Hex(`uuid:${seed}`).slice(0, 32);
  const version = '4';
  const variant = ((parseInt(hex.charAt(16), 16) & 0x3) | 0x8).toString(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${version}${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

/** Short opaque provider-style id, e.g. `cmp_3f9a1c2e`. */
export function opaqueId(prefix: string, seed: string, length = 10): string {
  return `${prefix}_${sha256Hex(`id:${seed}`).slice(0, length)}`;
}

// ---------------------------------------------------------------------------------------------
// Time helpers (fixtures are pinned to one instant so snapshots are reproducible)
// ---------------------------------------------------------------------------------------------

/** The "now" every fixture is generated relative to. */
export const FIXTURE_NOW_ISO = '2026-09-23T12:00:00.000Z';
export const FIXTURE_NOW_MS = Date.parse(FIXTURE_NOW_ISO);
const DAY_MS = 86_400_000;

/** ISO timestamp `days` before FIXTURE_NOW (fractional days allowed), millisecond precision. */
export function isoDaysAgo(days: number): string {
  return new Date(FIXTURE_NOW_MS - Math.round(days * DAY_MS)).toISOString();
}

/** ISO timestamp `days` after FIXTURE_NOW. */
export function isoDaysAhead(days: number): string {
  return new Date(FIXTURE_NOW_MS + Math.round(days * DAY_MS)).toISOString();
}

/** ISO timestamp with a random offset in [minDaysAgo, maxDaysAgo] days before FIXTURE_NOW. */
export function isoBetweenDaysAgo(rng: Rng, minDaysAgo: number, maxDaysAgo: number): string {
  const span = maxDaysAgo - minDaysAgo;
  return isoDaysAgo(minDaysAgo + rng.next() * span);
}

/** Date-only string (YYYY-MM-DD) `days` before FIXTURE_NOW. */
export function dateDaysAgo(days: number): string {
  return isoDaysAgo(days).slice(0, 10);
}

/** ASCII-only lowercase slug: strips accents, keeps [a-z0-9-]. */
export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
